import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import cls from "./StillCreatingProfile.module.scss";
import api from "@/utils/axios";
import { timeAgo } from "@/utils/timeAgo";
import { useCall } from "@/app/call-context-state";
import chatIcon from "@/assets/UI/chat.webp";
import audioCallIcon from "@/assets/UI/audioCall.webp";
import videoCallIcon from "@/assets/UI/videoCall.webp";
import emailIcon from "@/assets/UI/email.webp";
import myProfileIcon from "@/assets/UI/myProfile.webp";

const STATUS_LABELS = {
  local: "Local",
  avatar: "Avatar",
  gender: "Gender",
  relation: "Relationship",
  sexual_identity: "Sexual Identity",
  sexual_role: "Sexual Role",
  attraction_genders: "Attraction Genders",
  connection_types: "Connection Types",
  intimacy_types: "Intimacy Types",
  sexual_fantasies: "Sexual Fantasies",
  body_type: "Body Type",
  body_attract: "Body Attract",
  sexual_environment: "Sexual Environment",
  event_attract: "Event Attract",
  alcohol_cannabis: "Alcohol & Cannabis",
  be_matched: "Be Matched",
  join_host: "Join Host",
  real_image: "Real Image",
  social: "Social",
  payment: "Payment",
  pending: "Pending",
  approved: "Approved",
};

const StillCreatingProfile = () => {
  const navigate = useNavigate();
  const { startCall, activeCall, isConnecting } = useCall();

  const [members, setMembers] = useState([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
    total: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchMembers = async (p = 1) => {
    try {
      setLoading(true);
      const res = await api.get(
        `/v1/admin/members/members-still-creating-profile?page=${p}&limit=50`,
      );

      setMembers(res.data.data || []);
      setMeta({
        totalPages: res.data.totalPages,
        hasNextPage: res.data.hasNextPage,
        hasPrevPage: res.data.hasPrevPage,
        total: res.data.total,
      });
      setPage(res.data.page);
    } catch (err) {
      console.error("Error loading members still creating profile:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers(1);
  }, []);

  const handleStartCall = async (calleeId, callType) => {
    if (!calleeId) return;
    await startCall({ calleeId, callType });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "local":
        return cls.statusLocal;
      case "avatar":
        return cls.statusAvatar;
      case "gender":
        return cls.statusGender;
      case "relation":
        return cls.statusRelation;
      case "sexual_identity":
        return cls.statusSexualIdentity;
      case "sexual_role":
        return cls.statusSexualRole;
      case "attraction_genders":
        return cls.statusAttractionGenders;
      case "connection_types":
        return cls.statusConnectionTypes;
      case "intimacy_types":
        return cls.statusIntimacyTypes;
      case "sexual_fantasies":
        return cls.statusSexualFantasies;
      case "body_type":
        return cls.statusBodyType;
      case "body_attract":
        return cls.statusBodyAttract;
      case "sexual_environment":
        return cls.statusSexualEnvironment;
      case "event_attract":
        return cls.statusEventAttract;
      case "alcohol_cannabis":
        return cls.statusAlcoholCannabis;
      case "be_matched":
        return cls.statusBeMatched;
      case "join_host":
        return cls.statusJoinHost;
      case "real_image":
        return cls.statusRealImage;
      case "social":
        return cls.statusSocial;
      case "payment":
        return cls.statusPayment;
      case "pending":
        return cls.statusPending;
      case "approved":
        return cls.statusApproved;
      default:
        return cls.statusDefault;
    }
  };

  return (
    <div className={cls.container}>
      <div className={cls.headerRow}>
        <button
          type="button"
          className={cls.backBtn}
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
        <div className={cls.titleBlock}>
          <h2 className={cls.title}>Still Creating Profile</h2>
          <p className={cls.subtitle}>
            Active members who have started the onboarding flow but{" "}
            <strong>are not yet pending or approved.</strong>
          </p>
        </div>
      </div>

      <div className={cls.metaBar}>
        <span className={cls.chip}>
          Total in progress: <strong>{meta.total}</strong>
        </span>
        <span className={cls.chipSoft}>
          Page {page} / {meta.totalPages || 1}
        </span>
      </div>

      {loading ? (
        <div className={cls.loading}>
          <span className={cls.spinner} />
          <span>Loading members...</span>
        </div>
      ) : members.length === 0 ? (
        <div className={cls.empty}>
          <p>No members currently stuck in the onboarding flow ✨</p>
          <p className={cls.emptySub}>
            Everyone has either moved to pending / approved or not started yet.
          </p>
        </div>
      ) : (
        <>
          <div className={cls.tableWrap}>
            <table className={cls.table}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Username</th>
                  <th className={cls.actionsCol}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m, index) => (
                  <tr key={m.id}>
                    <td>{(page - 1) * 50 + index + 1}</td>
                    <td className={cls.usernameCell}>
                      <span className={cls.username}>@{m.username}</span>
                      <div className={cls.statusHint}>
                        {timeAgo(m.joinedAt)}
                      </div>
                      <span
                        className={`${cls.statusBadge} ${getStatusClass(
                          m.memberStatus,
                        )}`}
                      >
                        {STATUS_LABELS[m.memberStatus] || m.memberStatus}
                      </span>
                    </td>

                    <td className={cls.actionsCell}>
                      <button
                        type="button"
                        className={cls.chatBtn}
                        onClick={() => navigate(`/admin/chat/${m.id}`)}
                        aria-label={`Chat with ${m.username}`}
                      >
                        <img src={chatIcon} alt="" className={cls.chatIcon} />
                      </button>
                      <button
                        type="button"
                        className={cls.chatBtn}
                        onClick={() => handleStartCall(m.userId, "audio")}
                        disabled={!m.userId || isConnecting || !!activeCall}
                        aria-label={`Audio call ${m.username}`}
                      >
                        <img
                          src={audioCallIcon}
                          alt=""
                          className={cls.chatIcon}
                        />
                      </button>
                      <button
                        type="button"
                        className={cls.chatBtn}
                        onClick={() => handleStartCall(m.userId, "video")}
                        disabled={!m.userId || isConnecting || !!activeCall}
                        aria-label={`Video call ${m.username}`}
                      >
                        <img
                          src={videoCallIcon}
                          alt=""
                          className={cls.chatIcon}
                        />
                      </button>
                      <button
                        type="button"
                        className={cls.chatBtn}
                        onClick={() =>
                          navigate(`/admin/members/email/${m.userId}`)
                        }
                        aria-label={`Email ${m.username}`}
                      >
                        <img src={emailIcon} alt="" className={cls.chatIcon} />
                      </button>
                      <button
                        type="button"
                        className={cls.chatBtn}
                        onClick={() =>
                          navigate(`/admin/members/single/${m.userId}`)
                        }
                      >
                        <img
                          src={myProfileIcon}
                          alt=""
                          className={cls.chatIcon}
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={cls.pagination}>
            <button
              type="button"
              disabled={!meta.hasPrevPage || loading}
              onClick={() => fetchMembers(page - 1)}
            >
              ← Prev
            </button>
            <span>
              Page {page} / {meta.totalPages || 1}
            </span>
            <button
              type="button"
              disabled={!meta.hasNextPage || loading}
              onClick={() => fetchMembers(page + 1)}
            >
              Next →
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default StillCreatingProfile;
